import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { BookOpen } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { ListingReadOnlyAlert } from '@modules/_shared/components/ListingReadOnlyAlert';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import BrochureReportEditorWorkflow from '@modules/reports/brochure-report/BrochureReportEditor/BrochureReportEditorWorkflow';
import { useBrochureReport } from '@modules/reports/brochure-report/useBrochureReport';
import { getBrokerLanguage } from '@modules/reports/shared/language';
import { useLocale } from '@shared/hooks/locale';
import type { Language } from '@shared/LocaleContext';

import { BrochureSettings } from './BrochureSettings';
import { BrochureViewsCard } from './BrochureViewsCard';
import { LISTING_BROCHURE_ANCHORS } from './listingBrochureTour';

const BrochureNotFound = ({ preview = false }: { preview?: boolean }) => {
  const { t } = useLocale();

  return (
    <Stack
      sx={
        preview
          ? {
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'text.secondary',
            }
          : { alignItems: 'center', gap: 1, py: 2 }
      }
      color={preview ? undefined : 'text.secondary'}
    >
      <BookOpen size={preview ? 32 : 28} />
      <Typography variant="body2" color="text.secondary">
        {t('Listing brochure not found')}
      </Typography>
    </Stack>
  );
};

export const ListingBrochure = ({ readOnly }: { readOnly: boolean }) => {
  const { me } = useAppData();
  const { lotId = '' } = useParams();
  const tenantId = me?.tenant_id;
  const [currentError, setCurrentError] = useState<ErrorLike | null>(null);

  const {
    brochureReport,
    error: reportError,
    loading: reportLoading,
    refetch: refetchBrochure,
  } = useBrochureReport(lotId);
  const canEdit = !readOnly;
  useAutoTour('listing-brochure', !reportLoading, canEdit);

  const [previewLanguage, setPreviewLanguage] = useState<Language | null>(null);
  const effectiveLanguage = getBrokerLanguage(
    brochureReport?.broker,
    previewLanguage,
  );

  useEffect(() => {
    if (reportError != null) {
      setCurrentError(reportError);
    }
  }, [reportError]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        px: 2,
        py: 2,
        alignItems: { xs: 'flex-start', md: 'stretch' },
        flexGrow: 1,
        height: '100%',
        minHeight: 0,
        overflowY: { xs: 'auto', md: 'hidden' },
      }}
    >
      {/* Left column (1/3): views, buyer links, settings */}
      <Grid
        size={{ xs: 12, md: 4 }}
        sx={{ minHeight: 0, height: { md: '100%' }, overflowY: { md: 'auto' } }}
      >
        <Stack spacing={2}>
          <BrochureViewsCard
            lotId={lotId}
            templateId={brochureReport?.doc_template?.id ?? null}
            language={effectiveLanguage}
            readOnly={!canEdit}
          />

          {!reportLoading && brochureReport?.doc_template == null && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <BrochureNotFound />
            </Paper>
          )}

          {!reportLoading && brochureReport != null && canEdit && (
            <BrochureSettings
              listingId={lotId}
              listingBrochure={brochureReport}
              tenantId={tenantId}
              previewLanguage={effectiveLanguage}
              onPreviewLanguageChange={setPreviewLanguage}
              onTemplateContentChange={() => {
                refetchBrochure().catch(() => {});
              }}
            />
          )}

          {!reportLoading && brochureReport != null && !canEdit && (
            <ListingReadOnlyAlert />
          )}
        </Stack>
      </Grid>

      {/* Right column (2/3): brochure preview */}
      <Grid size={{ xs: 12, md: 8 }} sx={{ minHeight: 0 }}>
        <Paper
          {...tourAnchor(LISTING_BROCHURE_ANCHORS.preview)}
          elevation={0}
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            height: { xs: '70vh', md: '100%' },
          }}
        >
          {reportLoading ? (
            <Stack
              sx={{
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress disableShrink />
            </Stack>
          ) : brochureReport == null ? (
            <BrochureNotFound preview />
          ) : (
            <BrochureReportEditorWorkflow
              listingBrochure={brochureReport}
              previewOnly
              readOnly={!canEdit}
              language={effectiveLanguage}
            />
          )}
        </Paper>
      </Grid>
      <MutationErrorSnackbar
        error={currentError}
        onClose={() => setCurrentError(null)}
      />
    </Grid>
  );
};

export default ListingBrochure;
